import { useEffect, useMemo, useRef, useState } from 'react'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ComboboxItem } from '@node-space/ui-components'
import { useRoutingNumberValidation } from 'hooks/queries/Beneficiaries/useRoutingNumberValidation'
import { useBicValidation } from 'hooks/queries/useBicValidation'
import { useFilteredCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useIbanValidation } from 'hooks/queries/useIbanValidation'
import { useScanValidation } from 'hooks/queries/useScanValidation'
import { useSouthAfricanBanks } from 'hooks/queries/useSouthAfricanBanks'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { FieldsFetchedFromIbanValidation } from 'pages/Contacts/types/fiatInstruments'
import { getIsValidIban, resetBankAddress } from 'pages/Contacts/utils/fiatInstruments'
import { BeneficiaryDetails, BeneficiaryDetailsCurrency, PaymentMethod, PaymentMethods } from 'types/beneficiaries'
import { isIbanFormat } from 'utils/beneficiaries'
import { titleCase } from 'utils/utils'
import { useBankDetailValidation } from './useBankDetailsValidation'
import { useFormFieldOptions } from './useFiatFormFieldOptions'

interface UseBankDetailsFormProps {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  trigger: UseFormTrigger<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
  contactBeneficiary?: BeneficiaryDetails
}

export const useBankDetailsForm = ({
  formValues,
  errors,
  clearErrors,
  setValue,
  trigger,
  setError,
  contactBeneficiary,
}: UseBankDetailsFormProps) => {
  const { t } = useTranslation()
  const { setRequestError } = useMappedErrorMessage()
  const { accountTypeOptions } = useFormFieldOptions({ formValues })
  const { paymentMethods } = formValues || {}

  // State
  const [isValidIban, setIsValidIban] = useState<boolean>(false)
  const [isValidScan, setIsValidScan] = useState<boolean>(false)
  const [isValidRoutingNumber, setIsValidRoutingNumber] = useState<boolean>(false)
  const [isValidBic, setIsValidBic] = useState<boolean>(false)

  // Currency
  const isUsd = formValues?.currency === BeneficiaryDetailsCurrency.USD
  const isZar = formValues?.currency === BeneficiaryDetailsCurrency.ZAR
  const isGBP = formValues?.currency === BeneficiaryDetailsCurrency.GBP
  const isEur = formValues?.currency === BeneficiaryDetailsCurrency.EUR
  const isUSD = formValues?.currency === BeneficiaryDetailsCurrency.USD

  // Transfer type checks
  const isInternational =  useMemo(() => {
    const swiftPaymentMethod = paymentMethods?.find(paymentMethod => paymentMethod === PaymentMethods.SWIFT)
    return !!swiftPaymentMethod
  }, [paymentMethods])

  const isInternationalOnly = useMemo(() => {
    const swiftPaymentMethod = paymentMethods?.find(paymentMethod => paymentMethod === PaymentMethods.SWIFT)

    return !!swiftPaymentMethod && paymentMethods?.length === 1
  }, [paymentMethods, isInternational])

  const isLocal =  useMemo(() => {
    const nonSWIFTPaymentMethods = paymentMethods?.filter(paymentMethod => paymentMethod !== PaymentMethods.SWIFT)
    return !!nonSWIFTPaymentMethods?.length
  }, [paymentMethods])
  
  const isLocalOnly = useMemo(() => {
    const swiftPaymentMethod = paymentMethods?.find(paymentMethod => paymentMethod === PaymentMethods.SWIFT)

    return !swiftPaymentMethod && paymentMethods?.length > 0
  }, [paymentMethods])

  const isIban = useMemo(() => {
    return isIbanFormat(formValues?.accountNumber)
  }, [formValues?.accountNumber])

  const isCountryUs = formValues?.bankCountryCode === 'US'
  const isRoutingNumberVisible = (isUsd && isInternational && isCountryUs) || (isUsd && isInternational && !isInternationalOnly)

  // Validation hooks
  const {
    refetch: validateIban,
    isFetching: isValidatingIban,
    isError: isValidatingIbanError,
    data: ibanBankDetails,
  } = useIbanValidation(formValues?.accountNumber, 'IBAN', setRequestError, false)

  const {
    refetch: validateScan,
    isFetching: isValidatingScan,
    isError: isValidatingScanError,
    data: scanBankDetails,
  } = useScanValidation(formValues?.bankCode, formValues?.accountNumber, setRequestError, false)

  const {
    refetch: validateRoutingNumber,
    isFetching: isValidatingRoutingNumber,
    isError: isValidatingRoutingNumberError,
    data: routingNumberBankDetails,
  } = useRoutingNumberValidation(
    isLocalOnly ? formValues?.bankCode : formValues?.routingNumber,
    setRequestError,
    false
  )

  const {
    refetch: validateBic,
    isFetching: isValidatingBic,
    isError: isValidatingBicError,
    data: bicValidationData,
    error: bicValidationError,
  } = useBicValidation(formValues?.bankCode, setRequestError, false)

  const { data: countries, isPending: countriesLoading } = useFilteredCountriesQuery(isInternational)

  const {
    data: southAfricanBanks,
    isFetching: isFetchingSouthAfricanBanks,
    isError: isSouthAfricanBanksError,
  } = useSouthAfricanBanks(setRequestError, isZar)

  const isBankDetailsValidationLoading = isValidatingIban || isValidatingScan
  const scanSupportedPaymentMethods = scanBankDetails?.supportedPaymentMethods

  // Unified bank detail validation helper
  const updateFieldValue = (field: keyof FieldsFetchedFromIbanValidation, value: string) => {
    setValue(field, value)
  }

  const setIbanBankDetailsData = (details: FieldsFetchedFromIbanValidation) => {
    if (isLocalOnly && details?.bankCode) {
      setValue('swiftCode', details.bankCode)
      
      if (isEur || isGBP) {
        setValue('bankCode', details.bankCode)
      } 
    } else if (isInternational && Object.keys(details || {})?.length) {
      Object.keys(details)?.forEach(detail => {
        if (detail === 'bankCountry') {
          onInternationalCountrySelection(details[detail])
        } else if (detail === 'bankCode') {
            setValue('swiftCode', details[detail])
        } else {
          updateFieldValue(detail as keyof FieldsFetchedFromIbanValidation, details[detail])
        }
      })
    }
  }

  // Bank detail validation hook
  useBankDetailValidation({
    formValues,
    setValue,
    setError,
    clearErrors,
    ibanValidationProps: {
      isValidatingIbanError,
      ibanBankDetails,
      setIsValidIban,
      setIbanBankDetailsData,
    },
    routingNumberValidationProps: {
      isValidatingRoutingNumberError,
      routingNumberBankDetails,
      setIsRoutingNumberValid: setIsValidRoutingNumber,
    },
    scanValidationProps: {
      isValidatingScanError,
      scanBankDetails,
      setIsValidScan,
    },
    ...(isInternational && {
      bicValidationProps: {
        isValidatingBicError,
        bicValidationData,
        bicValidationError,
        setIsValidBic,
      },
    }),
    contactBeneficiary,
    ...(isInternational && {
      isIbanFormat: isIban,
      isValidIban,
    }),
  })

  // Effects
  useEffect(() => {
    if (contactBeneficiary || !isLocalOnly || isValidatingScan) return

    const {accountNumber, bankCode} = formValues ?? {}

    if (!accountNumber || !bankCode || !scanBankDetails) {
      setIsValidScan(false)
      return
    }

    const isFPSandChapsSupported =
      scanSupportedPaymentMethods?.includes('CHAPS') ||
      scanSupportedPaymentMethods?.includes('BACS') ||
      scanSupportedPaymentMethods?.includes('FASTER_PAYMENT') ||
      scanSupportedPaymentMethods?.includes('CCC_PAYMENTS')
    const hasValidationErrors = !!scanBankDetails?.validationFailed?.length
    const isValidGbpScan = isGBP && scanBankDetails && isFPSandChapsSupported && !hasValidationErrors

    if (!isValidGbpScan && formValues?.accountNumber && formValues?.bankCode) {
      setError('accountNumber', { message: t('reachabilityCheckErrorMessage') })
      setError('bankCode', { message: t('reachabilityCheckErrorMessage') })
      setIsValidScan(false)
    }
  }, [scanBankDetails, contactBeneficiary, isLocalOnly, isGBP, formValues, isValidatingScan])

  useEffect(() => {
    if (contactBeneficiary || !isEur) return

    clearErrors(['accountNumber'])

    if (!isBankDetailsValidationLoading && ibanBankDetails) {
      const isValidIban = getIsValidIban({
        paymentMethods: paymentMethods as PaymentMethod[],
        currency: formValues?.currency,
        ibanBankDetails,
      })
      setIsValidIban(isValidIban)

      const isSwift = paymentMethods?.includes(PaymentMethods.SWIFT)
      const isLocalAndSwift = isSwift && paymentMethods?.length > 1

      if (isEur) {
        if (isLocalAndSwift || isLocalOnly) {
          if (!isValidIban) {
            setError('accountNumber', { message: t('ibanSepaSupportError') })
            setValue('bankCode', '')
            return
          }
        }

        return
      }

      if (!isValidIban) {
        setError('accountNumber', { message: t('ibanValidationError') })
        setValue('bankCode', '')
        return
      }
      setValue('bankCode', ibanBankDetails?.bankAccount?.bankCode)
    }
  }, [
    ibanBankDetails,
    isValidatingIbanError,
    isBankDetailsValidationLoading,
    contactBeneficiary,
    isLocalOnly,
    isEur,
  ])

  useEffect(() => {
    if (contactBeneficiary || !isLocal || !isUsd) return
    if (!routingNumberBankDetails) return

    const { bankDetails, validationFailed } = routingNumberBankDetails
    const { bankAddress } = bankDetails ?? {}

    const isValid = !!bankAddress && !validationFailed?.length
    setIsValidRoutingNumber(isValid)

    if (isValid) {
      const { addressLine1, addressLine2, city, region, postCode } = bankAddress || {}
      setValue('bankAddressLine1', addressLine1)
      setValue('bankAddressLine2', addressLine2)
      setValue('bankCity', city)
      setValue('bankRegion', region)
      setValue('bankPostCode', postCode)
    } else {
      setError('bankCode', { message: t('abaRoutingNumberError') })
      resetLocalBankAddress()
    }
  }, [routingNumberBankDetails, formValues?.currency, contactBeneficiary, isLocalOnly, isUsd])

  useEffect(() => {
    if (!isInternational) return
    if (isValidatingRoutingNumberError && !formValues?.routingNumber) {
      resetBankAddress(setValue, 'bank')
    }
  }, [formValues?.routingNumber, isValidatingRoutingNumberError, isInternational])

  useEffect(() => {
    if (!isInternational) return
    if (isValidatingRoutingNumberError && formValues?.routingNumber) {
      setError('routingNumber', { message: t('abaRoutingNumberError') })
    }
  }, [isValidatingRoutingNumberError, formValues?.routingNumber, setError, t, isInternational])

  useEffect(() => {
    clearErrors()
  }, [formValues?.currency, clearErrors])

  const previousBankCodeRef = useRef<string | undefined>(formValues?.bankCode)

  useEffect(() => {
    const previousBankCode = previousBankCodeRef.current
    const currentBankCode = formValues?.bankCode
    previousBankCodeRef.current = currentBankCode

    if (previousBankCode && !currentBankCode) {
      setValue('bankName', '')
    }
  }, [formValues?.bankCode, setValue])

  useEffect(() => {
    if (isValidatingRoutingNumberError && isLocalOnly) {
      setError('bankCode', { message: t('abaRoutingNumberError') })
    }
  }, [isValidatingRoutingNumberError, isLocalOnly])

  // Handlers
  const resetLocalBankAddress = () => {
    setValue('bankName', '')
    setValue('bankAddressLine1', '')
    setValue('bankAddressLine2', '')
    setValue('bankCity', '')
    setValue('bankRegion', '')
    setValue('bankPostCode', '')
  }

  const onFieldUpdate =
    (fieldName: keyof ContactFiatInstrumentFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(fieldName, e.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

  const onAccountTypeChange = (value: string) => {
    setValue('accountType', value)
  }

  const handleAccountNumberOnBlur = async () => {
    if (contactBeneficiary) return
    clearErrors()

    const isValid = await trigger('accountNumber')

    if (!isValid) return

    const { accountNumber, bankCode, currency } = formValues || {}
    if (isLocalOnly) {
      switch (currency) {
        case BeneficiaryDetailsCurrency.GBP: {
          if (isIban) {
            validateIban()
            return
          }

          if (bankCode) {
            validateScan()
            return
          }
          break
        }
        case BeneficiaryDetailsCurrency.EUR: {
          if (isValid) {
            validateIban()
            return
          }
          break
        }
        case BeneficiaryDetailsCurrency.USD:
        default:
         return
      }
    } else if (isInternational) {
      if (!accountNumber || !isIban) return

      validateIban()
    }
  }

  const handleSortCodeOnBlur = async () => {
    if (contactBeneficiary || !isLocalOnly) return
    if (!formValues?.bankCode) return

    const isAccountNumberAndBankCodeValid = await trigger(['accountNumber', 'bankCode'])
    setIsValidScan(isAccountNumberAndBankCodeValid)

    if (isAccountNumberAndBankCodeValid) {
      validateScan()
    }
  }

  const handleRoutingNumberOnBlur = async () => {
    if (contactBeneficiary) return
    if (formValues?.currency !== BeneficiaryDetailsCurrency.USD) return
    if (!formValues?.bankCode && !formValues?.routingNumber) return

    await validateRoutingNumber()
  }

  const handleBankCodeOnBlur = async () => {
    if (contactBeneficiary) return
    clearErrors()

    if (!formValues?.bankCode) return

    if (isLocalOnly) {
      if (isGBP) {
        const isScanValid = await trigger(['accountNumber', 'bankCode'])
        setIsValidScan(isScanValid)

        if (isScanValid) {
          handleSortCodeOnBlur()
        }
        return
      }

      if (isUsd) {
        handleRoutingNumberOnBlur()
      }

      const isValid = await trigger('bankCode')
      if (!isValid) return
    } else if (isInternational) {
      if (isIbanFormat) return
      validateBic()
    }
  }

  const handleAccountNumberConfirmOnBlur = async () => {
    if (contactBeneficiary) return
    if (!formValues?.accountNumberConfirm) return

    clearErrors(['accountNumber', 'accountNumberConfirm'])

    if (formValues?.accountNumber && formValues?.accountNumberConfirm) {
      await trigger(['accountNumber', 'accountNumberConfirm'])
    }
  }

  const handleInternationalRoutingValidation = async () => {
    if (contactBeneficiary || !isInternational) return
    if (!formValues?.routingNumber) return
    if (formValues?.currency !== BeneficiaryDetailsCurrency.USD) return

    validateRoutingNumber()
  }

  const onInternationalCountrySelection = (countryCode: string) => {
    const countryName = internationalCountryList.find(
      country => country?.value === countryCode
    )?.leftText
    setValue('bankCountryName', countryName)
    setValue('bankCountryCode', countryCode)
  }

  const handleBankOnChange = (bankCode: string) => {
    setValue('bankNameCode', bankCode)
    const bankName = southAfricanBankOptions?.find(bank => bank?.value === bankCode)?.leftText
    if (bankName) {
      setValue('bankName', bankName as string)
    }
  }

  // Computed values
  const southAfricanBankOptions: ComboboxItem[] = useMemo(() => {
    if (!southAfricanBanks?.length) return []
    return southAfricanBanks?.map(bank => ({
      leftText: titleCase(bank?.name),
      value: bank?.id?.toString(),
    }))
  }, [southAfricanBanks])

  const internationalCountryList = useMemo(() => {
    if (countries?.length) {
      return countries?.map(country => ({
        leftText: country?.name,
        value: country?.code,
      }))
    }
    return []
  }, [countries])

  const isValidAccountNumber = useMemo(() => {
    if (isGBP && isLocalOnly) return isValidScan
    if (isEur) return isValidIban
    if (isInternational && isIban) return isValidIban
    return false
  }, [isValidIban, isValidScan, formValues?.currency, isLocalOnly, isInternational, isIban])

  const isValidBankCode = useMemo(() => {
    if (isGBP && isLocalOnly) return isValidScan
    if (isUsd && isLocal) return isValidRoutingNumber
    if (isInternational && isIban) return isValidIban
    if (isInternational && !isIban) return isValidBic
    return false
  }, [
    isValidRoutingNumber,
    isValidScan,
    isValidIban,
    isValidBic,
    formValues?.currency,
    isLocal,
    isLocalOnly,
    isInternational,
    isIban,
  ])

  const isAccountNumberAndBankCodeInline = useMemo(() => {
    if (isGBP) {
      if (isLocalOnly) return true

      if (isInternational && isLocal) return true

      return true
    }


    return (isLocal && (isEur || isGBP)) || isInternational
  }, [isLocal, isEur, isGBP, isInternational])

  const isAccountTypeRequired = useMemo(() => {
    if (isUsd)  return isLocal
    if (isZar) return true

    return false
  }, [isUsd, isInternationalOnly, formValues?.paymentMethods?.length, isZar, isLocal])

  return {
    // Currency/Transfer flags
    isUsd,
    isZar,
    isGBP,
    isUSD,
    isEur,
    isInternational,
    isInternationalOnly,
    isLocal,
    isLocalOnly,
    isIban,
    isCountryUs,
    isRoutingNumberVisible,
    isAccountNumberAndBankCodeInline,
    isAccountTypeRequired,

    // Validation states
    isValidIban,
    isValidScan,
    isValidRoutingNumber,
    isValidBic,
    isValidAccountNumber,
    isValidBankCode,

    // Loading states
    isBankDetailsValidationLoading,
    isValidatingRoutingNumber,
    isValidatingScan,
    isValidatingBic,
    countriesLoading,
    isFetchingSouthAfricanBanks,

    // Error states
    isSouthAfricanBanksError,

    // Options
    accountTypeOptions,
    southAfricanBankOptions,
    internationalCountryList,

    // Handlers
    onFieldUpdate,
    onAccountTypeChange,
    handleAccountNumberOnBlur,
    handleBankCodeOnBlur,
    handleAccountNumberConfirmOnBlur,
    handleInternationalRoutingValidation,
    onInternationalCountrySelection,
    handleBankOnChange,
  }
}

export type BankDetailsFormReturn = ReturnType<typeof useBankDetailsForm>
